import React from 'react';
import Icon from '../../../components/AppIcon';

const CallSummaryPanel = ({ summaryData }) => {
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}s ${mins}d ${secs}s`;
    }
    return `${mins}d ${secs}s`;
  };

  const formatPercentage = (value) => {
    return `%${value?.toFixed(1)}`;
  };

  const summaryCards = [
    {
      title: 'Toplam Arama',
      value: summaryData?.totalCalls?.toLocaleString('tr-TR'),
      icon: 'Phone',
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Ortalama Süre',
      value: formatDuration(summaryData?.averageDuration),
      icon: 'Clock',
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Başarı Oranı',
      value: formatPercentage(summaryData?.successRate),
      icon: 'CheckCircle',
      color: 'bg-emerald-500',
      textColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Toplam Süre',
      value: formatDuration(summaryData?.totalDuration),
      icon: 'Timer',
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const dispositionStats = [
    {
      label: 'Başarılı',
      count: summaryData?.dispositionBreakdown?.successful,
      percentage: (summaryData?.dispositionBreakdown?.successful / summaryData?.totalCalls) * 100,
      color: 'bg-green-500'
    },
    {
      label: 'Meşgul',
      count: summaryData?.dispositionBreakdown?.busy,
      percentage: (summaryData?.dispositionBreakdown?.busy / summaryData?.totalCalls) * 100,
      color: 'bg-yellow-500'
    },
    {
      label: 'Cevapsız',
      count: summaryData?.dispositionBreakdown?.noAnswer,
      percentage: (summaryData?.dispositionBreakdown?.noAnswer / summaryData?.totalCalls) * 100,
      color: 'bg-red-500'
    },
    {
      label: 'Geri Arama',
      count: summaryData?.dispositionBreakdown?.callback,
      percentage: (summaryData?.dispositionBreakdown?.callback / summaryData?.totalCalls) * 100,
      color: 'bg-blue-500'
    },
    {
      label: 'Yanlış Numara',
      count: summaryData?.dispositionBreakdown?.wrongNumber,
      percentage: (summaryData?.dispositionBreakdown?.wrongNumber / summaryData?.totalCalls) * 100,
      color: 'bg-gray-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards?.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">{card?.title}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{card?.value}</p>
              </div>
              <div className={`w-12 h-12 ${card?.bgColor} rounded-lg flex items-center justify-center`}>
                <Icon name={card?.icon} size={24} className={card?.textColor} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Disposition Breakdown */}
      <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Arama Sonuçları Dağılımı</h3>
        <div className="space-y-4">
          {dispositionStats?.map((stat, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${stat?.color}`}></div>
                <span className="text-sm font-medium text-slate-900">{stat?.label}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1 bg-slate-200 rounded-full h-2 w-32">
                  <div
                    className={`h-2 rounded-full ${stat?.color}`}
                    style={{ width: `${stat?.percentage}%` }}
                  ></div>
                </div>
                <div className="text-sm text-slate-600 w-16 text-right">
                  {stat?.count} ({formatPercentage(stat?.percentage)})
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Call Type Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Arama Türü Dağılımı</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="ArrowDown" size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-slate-900">Gelen Aramalar</span>
              </div>
              <div className="text-sm text-slate-600">
                {summaryData?.inboundCalls} ({formatPercentage((summaryData?.inboundCalls / summaryData?.totalCalls) * 100)})
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="ArrowUp" size={16} className="text-green-600" />
                <span className="text-sm font-medium text-slate-900">Giden Aramalar</span>
              </div>
              <div className="text-sm text-slate-600">
                {summaryData?.outboundCalls} ({formatPercentage((summaryData?.outboundCalls / summaryData?.totalCalls) * 100)})
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Kayıt Durumu</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="Mic" size={16} className="text-green-600" />
                <span className="text-sm font-medium text-slate-900">Kayıtlı Aramalar</span>
              </div>
              <div className="text-sm text-slate-600">
                {summaryData?.recordedCalls} ({formatPercentage((summaryData?.recordedCalls / summaryData?.totalCalls) * 100)})
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="MicOff" size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-900">Kayıtsız Aramalar</span>
              </div>
              <div className="text-sm text-slate-600">
                {summaryData?.totalCalls - summaryData?.recordedCalls} ({formatPercentage(((summaryData?.totalCalls - summaryData?.recordedCalls) / summaryData?.totalCalls) * 100)})
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Peak Hours */}
      <div className="bg-white rounded-lg shadow-subtle border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Yoğun Saatler</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summaryData?.peakHours?.map((hour, index) => (
            <div key={index} className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-lg font-bold text-slate-900">{hour?.time}</div>
              <div className="text-sm text-slate-600">{hour?.calls} arama</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CallSummaryPanel;